/**
 * Calculate gross profit from cost and sell price
 */
export const calculateGrossProfit = (cost: number, sell: number): number =>
  sell - cost;

/**
 * Calculate gross profit percentage (capped at 99.99%)
 */
export const calculateGrossProfitPercentage = (
  cost: number,
  sell: number
): number => {
  if (sell === 0) return 0;
  return Math.min(((sell - cost) / sell) * 100, 99.99);
};

/**
 * Calculate sell price from cost and desired GP percentage
 */
export const calculateSellFromGP = (
  cost: number,
  gpPercentage: number
): number => {
  if (gpPercentage >= 100) return cost; // Can't have 100% GP
  return cost / (1 - gpPercentage / 100);
};

/**
 * Calculate cost from sell price and GP percentage
 */
export const calculateCostFromGP = (
  sell: number,
  gpPercentage: number
): number => sell * (1 - gpPercentage / 100);

/**
 * Validate GP against min/max bounds
 */
export const validateGPBounds = (
  gpPercentage: number,
  minGP?: number | string,
  maxGP?: number | string
): {
  isValid: boolean;
  error?: string;
} => {
  const minGPNum = typeof minGP === 'string' ? parseFloat(minGP) : minGP;
  const maxGPNum = typeof maxGP === 'string' ? parseFloat(maxGP) : maxGP;

  if (minGPNum !== undefined && gpPercentage < minGPNum) {
    return {
      isValid: false,
      error: `GP must be at least ${minGPNum.toFixed(2)}%`
    };
  }

  if (maxGPNum !== undefined && gpPercentage > maxGPNum) {
    return {
      isValid: false,
      error: `GP cannot exceed ${maxGPNum.toFixed(2)}%`
    };
  }

  return { isValid: true };
};

/**
 * Format currency for display
 */
export const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);

/**
 * Format percentage for display
 */
export const formatPercentage = (value: number, decimals = 2): string =>
  `${value.toFixed(decimals)}%`;

/**
 * Calculate total line price (sell * quantity)
 */
export const calculateLineTotal = (sell: number, quantity: number): number =>
  sell * quantity;

/**
 * Calculate total line cost (cost * quantity)
 */
export const calculateLineCost = (cost: number, quantity: number): number =>
  cost * quantity;

/**
 * Check if price is editable based on product configuration
 */
export const isPriceEditable = (
  isEditable?: boolean,
  isFreeHand?: boolean
): boolean => isFreeHand === true || isEditable === true;

/**
 * Round to 2 decimal places
 */
export const roundToTwo = (num: number): number => Math.round(num * 100) / 100;

/**
 * Parse string to number safely
 */
export const safeParseNumber = (
  value: string | number,
  defaultValue = 0
): number => {
  if (typeof value === 'number') return value;

  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? defaultValue : parsed;
};
